import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt } from "@fortawesome/free-solid-svg-icons";
import { FloatInput, FloatNumericInput, FloatSelect } from "src/componentes";
import { MDFeCIOTOutput } from "src/services/Fiscal.Service";
import {
  ETipoOperacaoCIOT,
  formaPagamentoMDFeOptions,
  tipoCargaCIOTOptions,
  tipoOperacaoCIOTOptions,
  tipoPagamentoCIOTOptions,
  ufOptions,
} from "../../MDFe.Dto";
import MDFeModalGerarCIOTCode from "./MDFe.Modal.GerarCIOT.Code";
import { CiotValoresIniciais } from "./MDFe.Modal.GerarCIOT.Dto";

interface Props {
  show: boolean;
  inicial: CiotValoresIniciais | null;
  onClose: () => void;
  onGerado: (resultado: MDFeCIOTOutput) => void;
}

const eixosOptions = [
  { value: "", label: "Selecione" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
];

const Secao = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-1 mb-3">{titulo}</h6>
    {children}
  </div>
);

const MDFeModalGerarCIOTView = ({ show, inicial, onClose, onGerado }: Props) => {
  const code = MDFeModalGerarCIOTCode({ show, inicial, onClose, onGerado });
  const { form, submitting, exibeCarga, tipoOperacao } = code;
  const erros = form.formState.errors;
  const contingencia = form.watch("contingencia");

  const opcoesMunicipio = (lista?: { value: string; label: string }[]) => [
    { value: "", label: "Selecione" },
    ...(lista ?? []),
  ];

  return (
    <Modal
      show={show}
      onHide={code.handleClose}
      size="xl"
      centered
      scrollable
      backdrop="static"
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>
          <FontAwesomeIcon icon={faBolt} className="me-2 text-warning" />
          Gerar CIOT (ANTT)
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="small">
          Os campos abaixo foram copiados do MDF-e em preenchimento. Complete o que
          não existe no MDF-e (eixos, distância, natureza da carga, destinatário) e
          confirme. A geração <strong>declara a operação na ANTT</strong>.
        </Alert>

        <Secao titulo="Operação">
          <Row className="g-3">
            <Col xs={12} md={4}>
              <FloatSelect
                label="Tipo de Operação *"
                options={tipoOperacaoCIOTOptions}
                register={form.register("tipoOperacao")}
                errorsMessage={erros.tipoOperacao}
              />
            </Col>
            <Col xs={12} md={4}>
              <FloatInput
                label="CPF / CNPJ do Contratante (responsável) *"
                inputMask="cnpj-cpf"
                maxLength={18}
                readOnly
                registerField={form.register("documentoResponsavel")}
                errorsMessage={erros.documentoResponsavel}
              />
            </Col>
            {exibeCarga && (
              <Col xs={12} md={4}>
                <FloatInput
                  label="CPF / CNPJ do Destinatário *"
                  inputMask="cnpj-cpf"
                  maxLength={18}
                  registerField={form.register("documentoDestinatario")}
                  errorsMessage={erros.documentoDestinatario}
                />
              </Col>
            )}
            <Col xs={12} md={4}>
              <FloatInput
                label="RNTRC do Contratado *"
                inputMask="int"
                maxLength={9}
                registerField={form.register("rntrc")}
                errorsMessage={erros.rntrc}
              />
            </Col>
            <Col xs={12} md={4}>
              <FloatInput
                label="RNTRC do Contratante"
                inputMask="int"
                maxLength={9}
                registerField={form.register("rntrcContratante")}
                errorsMessage={erros.rntrcContratante}
              />
            </Col>
            <Col xs={12} md={4}>
              <FloatNumericInput
                label="Valor do Frete (R$) *"
                name="valorFrete"
                control={form.control}
                decimalScale={2}
                errorsMessage={erros.valorFrete as any}
              />
            </Col>
          </Row>
        </Secao>

        <Secao titulo="Viagem">
          <Row className="g-3">
            <Col xs={12} md={3}>
              <FloatInput
                label="Início da Viagem *"
                inputMask="data"
                registerField={form.register("dataInicioViagem")}
                errorsMessage={erros.dataInicioViagem}
              />
            </Col>
            <Col xs={12} md={3}>
              <FloatInput
                label="Fim da Viagem *"
                inputMask="data"
                registerField={form.register("dataFimViagem")}
                errorsMessage={erros.dataFimViagem}
              />
            </Col>
            <Col xs={12} md={3}>
              <FloatSelect
                label="UF Início *"
                options={ufOptions}
                register={form.register("ufInicio")}
                errorsMessage={erros.ufInicio}
              />
            </Col>
            <Col xs={12} md={3}>
              <FloatSelect
                label="UF Fim *"
                options={ufOptions}
                register={form.register("ufFim")}
                errorsMessage={erros.ufFim}
              />
            </Col>
            {exibeCarga && (
              <>
                <Col xs={12} md={4}>
                  <FloatSelect
                    label="Município de Partida *"
                    options={opcoesMunicipio(inicial?.opcoesMunicipioInicio)}
                    register={form.register("codigoMunicipioInicio")}
                    errorsMessage={erros.codigoMunicipioInicio}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <FloatSelect
                    label="Município de Chegada *"
                    options={opcoesMunicipio(inicial?.opcoesMunicipioFim)}
                    register={form.register("codigoMunicipioFim")}
                    errorsMessage={erros.codigoMunicipioFim}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <FloatInput
                    label="Distância (km) *"
                    inputMask="int"
                    maxLength={6}
                    registerField={form.register("distanciaKm")}
                    errorsMessage={erros.distanciaKm}
                  />
                </Col>
              </>
            )}
          </Row>
        </Secao>

        <Secao titulo="Veículos (1 a 5)">
          {erros.veiculos?.message && (
            <div className="text-danger small mb-2">{erros.veiculos.message}</div>
          )}
          {(erros.veiculos as any)?.root?.message && (
            <div className="text-danger small mb-2">
              {(erros.veiculos as any).root.message}
            </div>
          )}
          {code.veiculos.fields.map((field, index) => (
            <Row key={field.id} className="g-3 mb-2 align-items-start">
              <Col xs={12} md={3}>
                <FloatInput
                  label="Placa *"
                  inputMask="maiusculas"
                  maxLength={8}
                  registerField={form.register(`veiculos.${index}.placa`)}
                  errorsMessage={erros.veiculos?.[index]?.placa}
                />
              </Col>
              <Col xs={12} md={3}>
                <FloatSelect
                  label="Nº de Eixos *"
                  options={eixosOptions}
                  register={form.register(`veiculos.${index}.numeroEixos`)}
                  errorsMessage={erros.veiculos?.[index]?.numeroEixos}
                />
              </Col>
              <Col xs={12} md={4}>
                <FloatInput
                  label="RNTRC do Veículo"
                  inputMask="int"
                  maxLength={9}
                  registerField={form.register(`veiculos.${index}.rntrcVeiculo`)}
                  errorsMessage={erros.veiculos?.[index]?.rntrcVeiculo}
                />
              </Col>
              <Col xs={12} md={2} className="d-flex align-items-center">
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  disabled={code.veiculos.fields.length <= 1}
                  onClick={() => code.veiculos.remove(index)}
                >
                  Remover
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            disabled={code.veiculos.fields.length >= 5}
            onClick={() =>
              code.veiculos.append({ placa: "", numeroEixos: "", rntrcVeiculo: "" })
            }
          >
            + Adicionar Veículo
          </Button>
        </Secao>

        {exibeCarga && (
          <Secao titulo="Carga">
            <Row className="g-3">
              <Col xs={12} md={4}>
                <FloatInput
                  label="Natureza da Carga (ANTT) *"
                  inputMask="int"
                  maxLength={4}
                  tooltipMessage="Quatro primeiros dígitos do NCM do produto predominante"
                  registerField={form.register("codigoNaturezaCarga")}
                  errorsMessage={erros.codigoNaturezaCarga}
                />
              </Col>
              <Col xs={12} md={4}>
                <FloatNumericInput
                  label="Peso da Carga (kg) *"
                  name="pesoCarga"
                  control={form.control}
                  decimalScale={2}
                  errorsMessage={erros.pesoCarga as any}
                />
              </Col>
              <Col xs={12} md={4}>
                <FloatSelect
                  label="Tipo de Carga *"
                  options={[{ value: "", label: "Selecione" }, ...tipoCargaCIOTOptions]}
                  register={form.register("tipoCarga")}
                  errorsMessage={erros.tipoCarga}
                />
              </Col>
            </Row>

            {tipoOperacao === ETipoOperacaoCIOT.CargaFracionada && (
              <div className="mt-3">
                <div className="fw-semibold small text-secondary mb-2">
                  Contratantes da carga fracionada
                </div>
                {(erros.contratantesCargaFracionada as any)?.message && (
                  <div className="text-danger small mb-2">
                    {(erros.contratantesCargaFracionada as any).message}
                  </div>
                )}
                {(erros.contratantesCargaFracionada as any)?.root?.message && (
                  <div className="text-danger small mb-2">
                    {(erros.contratantesCargaFracionada as any).root.message}
                  </div>
                )}
                {code.contratantesFracionada.fields.map((field, index) => (
                  <Row key={field.id} className="g-3 mb-2 align-items-start">
                    <Col xs={12} md={6}>
                      <FloatInput
                        label="CPF / CNPJ *"
                        inputMask="cnpj-cpf"
                        maxLength={18}
                        registerField={form.register(
                          `contratantesCargaFracionada.${index}.documento`
                        )}
                        errorsMessage={
                          erros.contratantesCargaFracionada?.[index]?.documento
                        }
                      />
                    </Col>
                    <Col xs={12} md={2} className="d-flex align-items-center">
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={() => code.contratantesFracionada.remove(index)}
                      >
                        Remover
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={() => code.contratantesFracionada.append({ documento: "" })}
                >
                  + Adicionar Contratante
                </Button>
              </div>
            )}
          </Secao>
        )}

        <Secao titulo="Pagamentos">
          {(erros.pagamentos as any)?.message && (
            <div className="text-danger small mb-2">
              {(erros.pagamentos as any).message}
            </div>
          )}
          {(erros.pagamentos as any)?.root?.message && (
            <div className="text-danger small mb-2">
              {(erros.pagamentos as any).root.message}
            </div>
          )}
          {code.pagamentos.fields.map((field, index) => {
            const tipoPag = Number(form.watch(`pagamentos.${index}.tipoPagamento`));
            const forma = Number(form.watch(`pagamentos.${index}.formaPagamento`));
            const conta = [2, 3, 4].includes(tipoPag);
            const errPag = erros.pagamentos?.[index];

            return (
              <div key={field.id} className="mb-3 p-3 bg-light rounded border">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold text-secondary small">
                    Pagamento #{index + 1}
                  </span>
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    disabled={code.pagamentos.fields.length <= 1}
                    onClick={() => code.pagamentos.remove(index)}
                  >
                    Remover
                  </Button>
                </div>
                <Row className="g-3">
                  <Col xs={12} md={4}>
                    <FloatSelect
                      label="Tipo de Pagamento *"
                      options={tipoPagamentoCIOTOptions}
                      register={form.register(`pagamentos.${index}.tipoPagamento`)}
                      errorsMessage={errPag?.tipoPagamento}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <FloatSelect
                      label="Forma de Pagamento *"
                      options={formaPagamentoMDFeOptions}
                      register={form.register(`pagamentos.${index}.formaPagamento`)}
                      errorsMessage={errPag?.formaPagamento}
                    />
                  </Col>
                  <Col xs={12} md={5}>
                    <FloatInput
                      label="CPF / CNPJ do Recebedor *"
                      inputMask="cnpj-cpf"
                      maxLength={18}
                      registerField={form.register(`pagamentos.${index}.cpfCnpjCreditado`)}
                      errorsMessage={errPag?.cpfCnpjCreditado}
                    />
                  </Col>

                  {tipoPag === 6 && (
                    <Col xs={12} md={6}>
                      <FloatInput
                        label="Chave Pix *"
                        maxLength={77}
                        registerField={form.register(`pagamentos.${index}.chavePix`)}
                        errorsMessage={errPag?.chavePix}
                      />
                    </Col>
                  )}

                  {conta && (
                    <>
                      <Col xs={12} md={4}>
                        <FloatInput
                          label="Banco (código) *"
                          inputMask="int"
                          maxLength={3}
                          registerField={form.register(
                            `pagamentos.${index}.codigoInstituicaoFinanceira`
                          )}
                          errorsMessage={errPag?.codigoInstituicaoFinanceira}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <FloatInput
                          label="Agência *"
                          maxLength={10}
                          registerField={form.register(`pagamentos.${index}.numeroAgencia`)}
                          errorsMessage={errPag?.numeroAgencia}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <FloatInput
                          label="Conta *"
                          maxLength={20}
                          registerField={form.register(`pagamentos.${index}.numeroConta`)}
                          errorsMessage={errPag?.numeroConta}
                        />
                      </Col>
                    </>
                  )}

                  {forma === 1 && (
                    <>
                      <Col xs={12} md={3}>
                        <FloatInput
                          label="Nº da Parcela *"
                          inputMask="int"
                          maxLength={3}
                          registerField={form.register(`pagamentos.${index}.numeroParcela`)}
                          errorsMessage={errPag?.numeroParcela}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <FloatInput
                          label="Vencimento *"
                          inputMask="data"
                          registerField={form.register(`pagamentos.${index}.dataVencimento`)}
                          errorsMessage={errPag?.dataVencimento}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <FloatNumericInput
                          label="Valor da Parcela (R$) *"
                          name={`pagamentos.${index}.valorParcela`}
                          control={form.control}
                          decimalScale={2}
                          errorsMessage={errPag?.valorParcela as any}
                        />
                      </Col>
                    </>
                  )}
                </Row>
              </div>
            );
          })}
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() =>
              code.pagamentos.append({
                tipoPagamento: "6",
                formaPagamento: "0",
                cpfCnpjCreditado: "",
                chavePix: "",
              })
            }
          >
            + Adicionar Pagamento
          </Button>
        </Secao>

        {tipoOperacao === ETipoOperacaoCIOT.CargaLotacao && (
          <Secao titulo="Indicadores">
            <Row className="g-3">
              <Col xs={12} md={4}>
                <Form.Check
                  type="switch"
                  id="ciot-ind-alto-desempenho"
                  label="Alto desempenho"
                  {...form.register("indAltoDesempenho")}
                />
              </Col>
              <Col xs={12} md={4}>
                <Form.Check
                  type="switch"
                  id="ciot-ind-retorno-vazio"
                  label="Retorno vazio"
                  {...form.register("indRetornoVazio")}
                />
              </Col>
              <Col xs={12} md={4}>
                <Form.Check
                  type="switch"
                  id="ciot-ind-composicao-veicular"
                  label="Composição veicular"
                  {...form.register("composicaoVeicular")}
                />
              </Col>
            </Row>
          </Secao>
        )}

        <Secao titulo="Avançado">
          <Row className="g-3">
            <Col xs={12} md={4}>
              <Form.Check
                type="switch"
                id="ciot-contingencia"
                label="Contingência"
                {...form.register("contingencia")}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Check
                type="switch"
                id="ciot-forcar-novo"
                label="Forçar novo CIOT (ignorar pendente recente)"
                {...form.register("forcarNovo")}
              />
            </Col>
            {contingencia && (
              <>
                <Col xs={12} md={4}>
                  <FloatInput
                    label="ID da Operação de Transporte (12) *"
                    maxLength={12}
                    registerField={form.register("idOperacaoTransporte")}
                    errorsMessage={erros.idOperacaoTransporte}
                  />
                </Col>
                <Col xs={12}>
                  <FloatInput
                    label="Justificativa da Contingência *"
                    inputMask="textarea"
                    maxLength={255}
                    registerField={form.register("justificativaContingencia")}
                    errorsMessage={erros.justificativaContingencia}
                  />
                </Col>
              </>
            )}
          </Row>
        </Secao>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="button"
          variant="secondary"
          onClick={code.handleClose}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => code.handleGerar()}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Gerando CIOT na ANTT...
            </>
          ) : (
            "Gerar CIOT"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MDFeModalGerarCIOTView;
